import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Rideable } from '../interfaces/Rideable';

export class LogTurtle implements GameObject, Rideable {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;
  
  private speed: number;
  private isLog: boolean;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private gameWidth: number;
  
  // Visual properties
  private width: number = 64;
  private height: number = 24;
  private animationTimer: number = 0;
  
  // Turtle specific properties
  private divingTimer: number = 0;
  private isDiving: boolean = false;
  private readonly DIVE_CYCLE: number = 8.0; // 8 seconds cycle
  private readonly DIVE_WARNING: number = 6.0; // Start warning at 6 seconds
  
  constructor(
    id: string,
    x: number,
    y: number,
    speed: number,
    isLog: boolean,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.speed = speed;
    this.isLog = isLog;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    
    this.setupProperties();
    this.size = { x: this.width, y: this.height };
  }

  private setupProperties(): void {
    if (this.isLog) {
      // Logs are longer and provide stable platforms
      this.width = Math.random() < 0.3 ? 96 : 64; // 30% chance for long log (3 cells), otherwise 2 cells
      this.height = 24;
    } else {
      // Turtles are smaller groups
      this.width = Math.random() < 0.4 ? 64 : 32; // 40% chance for 2 turtles, otherwise 1
      this.height = 26;
      
      // Randomize diving cycle start time
      this.divingTimer = Math.random() * this.DIVE_CYCLE;
    }
  }

  public update(deltaTime: number): void {
    // Move horizontally
    this.position.x += this.speed * deltaTime * 60; // Normalize for 60 FPS
    
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Update turtle diving behavior
    if (!this.isLog) {
      this.divingTimer += deltaTime;
      
      if (this.divingTimer >= this.DIVE_CYCLE) {
        this.divingTimer = 0;
        this.isDiving = false;
      } else if (this.divingTimer >= this.DIVE_WARNING) {
        // Turtles start diving after warning period
        if (this.divingTimer >= this.DIVE_WARNING + 1.0) {
          this.isDiving = true;
        }
      }
    }
    
    // Mark as inactive if far off screen
    if (this.speed > 0 && this.position.x > this.gameWidth + 200) {
      this.active = false;
    } else if (this.speed < 0 && this.position.x < -200) {
      this.active = false;
    }
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.width,
      height: this.height
    };
  }

  public getSpeed(): number {
    return this.speed;
  }

  public isSafe(): boolean {
    // Logs are always safe, turtles are unsafe when diving
    return this.isLog || !this.isDiving;
  }

  public isWarning(): boolean {
    // Only turtles give warnings before diving
    return !this.isLog && this.divingTimer >= this.DIVE_WARNING && this.divingTimer < this.DIVE_WARNING + 1.0;
  }

  public checkCollision(otherBounds: Rectangle): boolean {
    // Only allow collision if the object is safe to stand on
    if (!this.isSafe()) {
      return false;
    }
    
    const myBounds = this.getBounds();
    
    return myBounds.x < otherBounds.x + otherBounds.width &&
           myBounds.x + myBounds.width > otherBounds.x &&
           myBounds.y < otherBounds.y + otherBounds.height &&
           myBounds.y + myBounds.height > otherBounds.y;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Note: Offsets will be handled by the game when calling this method
    if (this.isLog) {
      this.renderLog(this.position.x, this.position.y);
    } else {
      this.renderTurtle(this.position.x, this.position.y);
    }
  }

  public renderWithOffset(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
    const renderX = offsetX + this.position.x;
    const renderY = offsetY + this.position.y;
    
    if (this.isLog) {
      this.renderLog(renderX, renderY);
    } else {
      this.renderTurtle(renderX, renderY);
    }
  }

  private renderLog(x: number, y: number): void {
    // Main log body - brown cylinder
    const logColor = '#8B4513'; // Saddle brown
    this.renderManager.fillRect(x, y + 4, this.width, this.height - 8, logColor);
    
    // Add wood texture with darker lines
    const darkBrown = '#654321';
    for (let i = 0; i < 3; i++) {
      const lineY = y + 8 + i * 4;
      this.renderManager.drawLine(x, lineY, x + this.width, lineY, darkBrown, 1);
    }
    
    // Log ends (circular)
    this.renderManager.drawEllipse(x - 2, y + 4, 4, (this.height - 8) / 2, logColor, true);
    this.renderManager.drawEllipse(x + this.width - 6, y + 4, 4, (this.height - 8) / 2, logColor, true);
    
    // Add rings on the ends
    this.renderManager.drawEllipse(x - 2, y + 4, 4, (this.height - 8) / 2, darkBrown, false, 1);
    this.renderManager.drawEllipse(x + this.width - 6, y + 4, 4, (this.height - 8) / 2, darkBrown, false, 1);
    
    // Add some knots/texture
    const knotPositions = this.width > 70 ? [20, 50, 70] : [16, 40];
    knotPositions.forEach(knotX => {
      if (knotX < this.width - 10) {
        this.renderManager.drawEllipse(x + knotX, y + 8, 3, 3, darkBrown, true);
      }
    });
  }

  private renderTurtle(x: number, y: number): void {
    const numTurtles = this.width > 50 ? 2 : 1;
    const turtleWidth = this.width / numTurtles;
    
    for (let i = 0; i < numTurtles; i++) {
      const turtleX = x + i * turtleWidth;
      this.renderSingleTurtle(turtleX, y, turtleWidth);
    }
  }

  private renderSingleTurtle(x: number, y: number, width: number): void {
    if (this.isDiving) {
      // Draw underwater bubbles instead of turtle
      this.renderBubbles(x, y, width);
      return;
    }
    
    const centerX = x + width / 2;
    const centerY = y + this.height / 2;
    
    // Turtle shell (main body)
    let shellColor = '#228B22'; // Forest green
    
    // Warning color (flash yellow/green when about to dive)
    if (this.isWarning()) {
      const flashSpeed = 10;
      const flash = Math.sin(this.animationTimer * flashSpeed) > 0;
      shellColor = flash ? '#FFFF00' : '#228B22';
    }
    
    // Main shell
    this.renderManager.drawEllipse(
      centerX - width * 0.35,
      centerY - this.height * 0.25,
      width * 0.35,
      this.height * 0.25,
      shellColor,
      true
    );
    
    // Shell pattern (hexagonal segments)
    const darkGreen = '#006400';
    const segmentSize = 6;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue;
        const segX = centerX + dx * segmentSize;
        const segY = centerY + dy * segmentSize * 0.6;
        
        this.renderManager.drawEllipse(
          segX - segmentSize / 2,
          segY - segmentSize / 2,
          segmentSize / 2,
          segmentSize / 2,
          darkGreen,
          false,
          1
        );
      }
    }
    
    // Head
    const headX = centerX + (this.speed > 0 ? width * 0.25 : -width * 0.25);
    const headY = centerY - 2;
    this.renderManager.drawEllipse(headX - 4, headY - 3, 4, 3, '#32CD32', true); // Lime green
    
    // Eyes
    this.renderManager.drawEllipse(headX - 2, headY - 1, 1, 1, '#000000', true);
    this.renderManager.drawEllipse(headX + 1, headY - 1, 1, 1, '#000000', true);
    
    // Flippers (simple ovals)
    const flipperColor = '#32CD32';
    
    // Front flippers
    this.renderManager.drawEllipse(
      centerX - width * 0.4,
      centerY - 6,
      4,
      2,
      flipperColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + width * 0.3,
      centerY - 6,
      4,
      2,
      flipperColor,
      true
    );
    
    // Back flippers
    this.renderManager.drawEllipse(
      centerX - width * 0.35,
      centerY + 4,
      4,
      2,
      flipperColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + width * 0.25,
      centerY + 4,
      4,
      2,
      flipperColor,
      true
    );
  }

  private renderBubbles(x: number, y: number, width: number): void {
    // Draw bubbles where the turtle was
    const bubbleCount = 4 + Math.floor(Math.random() * 3);
    
    for (let i = 0; i < bubbleCount; i++) {
      const bubbleX = x + 8 + (Math.random() * (width - 16));
      const bubbleY = y + 8 + (Math.random() * (this.height - 16));
      const bubbleSize = 2 + Math.random() * 4;
      
      // Animate bubbles rising
      const riseOffset = (this.animationTimer * 20 + i * 10) % 30;
      const adjustedY = bubbleY - riseOffset;
      
      if (adjustedY > y - 10) {
        this.renderManager.drawEllipse(
          bubbleX - bubbleSize / 2,
          adjustedY - bubbleSize / 2,
          bubbleSize / 2,
          bubbleSize / 2,
          '#87CEEB', // Sky blue
          false,
          1
        );
      }
    }
  }
}